import {
  dateToYYYYMMDD,
  formatDate,
  range,
  rangeNonRecursive
} from "app/DealMachineCore/actions/CommonFunctions";
import api from "app/DealMachineCore/apis/DealMachineAPIV2";
import {
  Bold,
  Button,
  Card,
  CardBody,
  CenterCenter,
  Copy,
  Icon,
  Row,
  Scroll,
  Spin,
  Split,
  Stretch,
  Title,
  Wrapper
} from "app/NativeComponents/common";
import { PillButton, Select } from "app/NativeComponents/snippets";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CompDateSelect } from "./CompDateSelect";
import { CompItem } from "./CompItem";
import { CompMap } from "./CompMap";

const dm_api = api.create();

export const CompsUI = ({ property }) => {
  const { token } = useSelector(({ auth }) => auth);
  const { colors } = useSelector(({ settings }) => settings);

  const [date1, setDate1] = useState(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 6,
      new Date().getDate()
    )
  );
  const [date2, setDate2] = useState(new Date());
  const [searchRange, setSearchRange] = useState(3);
  const [sqftMin, setSqftMin] = useState(
    Math.round(Math.ceil(Number(property.building_square_feet) * 0.9) / 100) *
      100
  );
  const [sqftMax, setSqftMax] = useState(
    Math.round(Math.ceil(Number(property.building_square_feet) * 1.1) / 100) *
      100
  );
  const [bedRoomMin, setBedRoomMin] = useState();
  const [bedRoomMax, setBedRoomMax] = useState();
  const [bathRoomMin, setBathRoomMin] = useState();
  const [bathRoomMax, setBathRoomMax] = useState();
  const [loadingComps, setLoadingComps] = useState(true);
  const [comps, setComps] = useState([]);
  const [selectedComps, setSelectedComps] = useState([]); // ID

  const [showSearchCriteria, setShowSearchCriteria] = useState(false);
  const [expandSaleDateFilter, setExpandSaleDateFilter] = useState(false);
  const [expandRangeFilter, setExpandRangeFilter] = useState(false);
  const [expandSqftFilter, setExpandSqftFilter] = useState(false);
  const [expandBedroomFilter, setExpandBedroomFilter] = useState(false);
  const [expandBathroomFilter, setExpandBathroomFilter] = useState(false);

  useEffect(() => {
    const getComps = async () => {
      setLoadingComps(true);
      const criteria = {
        token,
        property_id: property.id,
        sale_date_min: dateToYYYYMMDD(date1),
        sale_date_max: dateToYYYYMMDD(date2),
        sqft_min: sqftMin,
        sqft_max: sqftMax,
        bedroom_min: bedRoomMin,
        bedroom_max: bedRoomMax,
        bathroom_min: bathRoomMin,
        bathroom_max: bathRoomMax,
        range_miles: searchRange
      };
      const result = await dm_api.getPropertyComps(criteria);
      if (result.data && result.data.data && !result.problem)
        setComps(
          result.data.data.sort((a, b) =>
            Number(a.distance || 0) > Number(b.distance || 0) ? 1 : -1
          )
        );
      setLoadingComps(false);
    };
    getComps();
  }, [
    property,
    date1,
    date2,
    searchRange,
    bedRoomMin,
    bedRoomMax,
    bathRoomMin,
    bathRoomMax,
    sqftMin,
    sqftMax
  ]);

  useEffect(() => {
    if (showSearchCriteria) return;
    setExpandSaleDateFilter(false);
    setExpandRangeFilter(false);
    setExpandSqftFilter(false);
    setExpandBedroomFilter(false);
    setExpandBathroomFilter(false);
  }, [showSearchCriteria]);

  return (
    <Wrapper
      style={{
        height: "100%",
        flex: 1
      }}
    >
      <Wrapper
        style={{
          ...{
            width: "100%",
            marginBottom: 6,
            overflow: "hidden"
          },
          ...(!showSearchCriteria ? { height: 60 } : {})
        }}
      >
        <Row
          style={{
            paddingLeft: 12,
            paddingRight: 12,
            justifyContent: showSearchCriteria ? "flex-end" : "space-between"
          }}
        >
          {!showSearchCriteria && (
            <Copy style={{ width: "60%" }}>
              Showing properties sold between <Bold>{formatDate(date1)}</Bold>{" "}
              and <Bold>{formatDate(date2)}</Bold> within{" "}
              <Bold>{searchRange} miles</Bold>.
            </Copy>
          )}
          <Button
            onPress={() => setShowSearchCriteria(!showSearchCriteria)}
            style={{ alignItems: "center", justifyContent: "center" }}
          >
            <Card
              style={{
                borderRadius: 20,
                height: 30,
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Wrapper
                style={{
                  padding: 0,
                  paddingRight: 15,
                  paddingLeft: 15,
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Row>
                  <Copy style={{ marginRight: 5 }}>
                    {showSearchCriteria ? "Hide Filters" : "Show Filters"}
                  </Copy>
                  <Icon
                    size={18}
                    icon={`keyboard-arrow-${
                      showSearchCriteria ? "up" : "down"
                    }`}
                  />
                </Row>
              </Wrapper>
            </Card>
          </Button>
        </Row>
        {showSearchCriteria && showSearchCriteria && (
          <Wrapper style={{ height: "100%" }}>
            {/* Date Filter */}
            <Card>
              <CardBody>
                <Button onPress={() => setExpandSaleDateFilter(v => !v)}>
                  <Split>
                    <Stretch>
                      <Title>Date of sale</Title>
                      {!expandSaleDateFilter && (
                        <Copy>
                          {formatDate(date1)} to {formatDate(date2)}
                        </Copy>
                      )}
                    </Stretch>

                    <Wrapper>
                      <CenterCenter>
                        <Icon
                          style={{ marginLeft: 10 }}
                          icon={`keyboard-arrow-${
                            expandSaleDateFilter ? "up" : "down"
                          }`}
                        />
                      </CenterCenter>
                    </Wrapper>
                  </Split>
                </Button>
                {expandSaleDateFilter && (
                  <Wrapper style={{ marginTop: 15 }}>
                    <Row style={{ justifyContent: "center" }}>
                      <CompDateSelect
                        date={date1}
                        onDateChange={setDate1}
                        label="Min Date of Sale"
                      />
                      <Copy>and</Copy>
                      <CompDateSelect
                        date={date2}
                        onDateChange={setDate2}
                        label="Max Date of Sale"
                      />
                    </Row>
                  </Wrapper>
                )}
              </CardBody>
            </Card>
            {/* Range Filter */}
            <Card>
              <CardBody>
                <Button onPress={() => setExpandRangeFilter(v => !v)}>
                  <Split>
                    <Stretch>
                      <Title>Range</Title>
                      {!expandRangeFilter && (
                        <Copy>Within {searchRange} miles</Copy>
                      )}
                    </Stretch>
                    <Wrapper>
                      <CenterCenter>
                        <Icon
                          style={{ marginLeft: 10 }}
                          icon={`keyboard-arrow-${
                            expandRangeFilter ? "up" : "down"
                          }`}
                        />
                      </CenterCenter>
                    </Wrapper>
                  </Split>
                </Button>
                {expandRangeFilter && (
                  <Wrapper style={{ marginTop: 15 }}>
                    <Row
                      style={{
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <Copy>within</Copy>
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { label: "1 mile", value: 1 },
                            { label: "2 miles", value: 2 },
                            { label: "3 miles", value: 3 },
                            { label: "4 miles", value: 4 },
                            { label: "5 miles", value: 5 }
                          ]}
                          text={<Bold>{searchRange} miles</Bold>}
                          value={searchRange}
                          onSelect={setSearchRange}
                        />
                      </PillButton>
                    </Row>
                  </Wrapper>
                )}
              </CardBody>
            </Card>
            {/* Sqft Filter */}
            <Card>
              <CardBody>
                <Button onPress={() => setExpandSqftFilter(v => !v)}>
                  <Split>
                    <Stretch>
                      <Title>Square Feet</Title>
                      {!expandSqftFilter &&
                        (sqftMin || sqftMax ? (
                          sqftMin === sqftMax ? (
                            <Copy>{sqftMin}sq ft</Copy>
                          ) : (
                            <Copy>
                              {sqftMin || "Any"} to {sqftMax || "Any"}
                            </Copy>
                          )
                        ) : (
                          <Copy>Any</Copy>
                        ))}
                    </Stretch>
                    <Wrapper>
                      <CenterCenter>
                        <Icon
                          style={{ marginLeft: 10 }}
                          icon={`keyboard-arrow-${
                            expandSqftFilter ? "up" : "down"
                          }`}
                        />
                      </CenterCenter>
                    </Wrapper>
                  </Split>
                </Button>
                {expandSqftFilter && (
                  <Wrapper style={{ marginTop: 15 }}>
                    <Row
                      style={{
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...rangeNonRecursive(
                              100,
                              Math.min(sqftMax || 10000, 10000),
                              100
                            ).map(n => ({
                              label: `${n} sq ft${
                                n ===
                                Math.round(
                                  Number(property.building_square_feet) / 100
                                ) *
                                  100
                                  ? " (Current property)"
                                  : ""
                              }`,
                              value: n
                            }))
                          ]}
                          text={
                            sqftMin ? (
                              <Bold>{sqftMin} sq ft</Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={sqftMin}
                          onSelect={setSqftMin}
                        />
                      </PillButton>
                      <Copy>to</Copy>
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...rangeNonRecursive(
                              Math.max(100, sqftMin || 100),
                              10000,
                              100
                            ).map(n => ({
                              label: `${n} sq ft${
                                n ===
                                Math.round(
                                  Number(property.building_square_feet) / 100
                                ) *
                                  100
                                  ? " (Current property)"
                                  : ""
                              }`,
                              value: n
                            }))
                          ]}
                          text={
                            sqftMax ? (
                              <Bold>{sqftMax} sq ft</Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={sqftMax}
                          onSelect={setSqftMax}
                        />
                      </PillButton>
                    </Row>
                  </Wrapper>
                )}
              </CardBody>
            </Card>
            {/* Bedroom Filter */}
            <Card>
              <CardBody>
                <Button onPress={() => setExpandBedroomFilter(v => !v)}>
                  <Split>
                    <Stretch>
                      <Title>Bedrooms</Title>
                      {!expandBedroomFilter &&
                        (bedRoomMin || bedRoomMax ? (
                          bedRoomMin === bedRoomMax ? (
                            <Copy>{bedRoomMin}</Copy>
                          ) : (
                            <Copy>
                              {bedRoomMin || "Any"} to {bedRoomMax || "Any"}
                            </Copy>
                          )
                        ) : (
                          <Copy>Any</Copy>
                        ))}
                    </Stretch>
                    <Wrapper>
                      <CenterCenter>
                        <Icon
                          style={{ marginLeft: 10 }}
                          icon={`keyboard-arrow-${
                            expandBedroomFilter ? "up" : "down"
                          }`}
                        />
                      </CenterCenter>
                    </Wrapper>
                  </Split>
                </Button>
                {expandBedroomFilter && (
                  <Wrapper style={{ marginTop: 15 }}>
                    <Row
                      style={{
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...range(1, Math.min(bedRoomMax || 8, 8)).map(
                              n => ({
                                label: `${n} bedroom${n > 1 ? "s" : ""} ${
                                  n === Number(property.total_bedrooms)
                                    ? "(Current property)"
                                    : ""
                                }`,
                                value: n
                              })
                            )
                          ]}
                          text={
                            bedRoomMin ? (
                              <Bold>
                                {bedRoomMin} bedroom{bedRoomMin > 1 && "s"}
                              </Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={bedRoomMin}
                          onSelect={setBedRoomMin}
                        />
                      </PillButton>
                      <Copy>to</Copy>
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...range(Math.max(1, bedRoomMin || 1), 8).map(
                              n => ({
                                label: `${n} bedroom${n > 1 ? "s" : ""} ${
                                  n === Number(property.total_bedrooms)
                                    ? " (Current property)"
                                    : ""
                                }`,
                                value: n
                              })
                            )
                          ]}
                          text={
                            bedRoomMax ? (
                              <Bold>
                                {bedRoomMax} bedroom{bedRoomMax > 1 && "s"}
                              </Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={bedRoomMax}
                          onSelect={setBedRoomMax}
                        />
                      </PillButton>
                    </Row>
                  </Wrapper>
                )}
              </CardBody>
            </Card>
            {/* Bathroom Filter */}
            <Card>
              <CardBody>
                <Button onPress={() => setExpandBathroomFilter(v => !v)}>
                  <Split>
                    <Stretch>
                      <Title>Bathrooms</Title>
                      {!expandBathroomFilter &&
                        (bathRoomMin || bathRoomMax ? (
                          bathRoomMin === bathRoomMax ? (
                            <Copy>{bedRoomMin}</Copy>
                          ) : (
                            <Copy>
                              {bathRoomMin || "Any"} to {bathRoomMax || "Any"}
                            </Copy>
                          )
                        ) : (
                          <Copy>Any</Copy>
                        ))}
                    </Stretch>
                    <Wrapper>
                      <CenterCenter>
                        <Icon
                          style={{ marginLeft: 10 }}
                          icon={`keyboard-arrow-${
                            expandBathroomFilter ? "up" : "down"
                          }`}
                        />
                      </CenterCenter>
                    </Wrapper>
                  </Split>
                </Button>
                {expandBathroomFilter && (
                  <Wrapper style={{ marginTop: 15 }}>
                    <Row
                      style={{
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...range(1, Math.min(bathRoomMax || 6, 6), 0.5).map(
                              n => ({
                                label: `${n} bathroom${n > 1 ? "s" : ""} ${
                                  n === Number(property.total_baths)
                                    ? " (Current property)"
                                    : ""
                                }`,
                                value: n
                              })
                            )
                          ]}
                          text={
                            bathRoomMin ? (
                              <Bold>
                                {bathRoomMin} bathroom{bathRoomMin > 1 && "s"}
                              </Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={bathRoomMin}
                          onSelect={setBathRoomMin}
                        />
                      </PillButton>
                      <Copy>to</Copy>
                      <PillButton
                        innerStyle={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 15,
                          paddingRight: 15
                        }}
                        onPress={() => {}}
                      >
                        <Select
                          label="Any"
                          item_ref={"state"}
                          size="small"
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          items={[
                            { value: "", label: "Any" },
                            ...range(Math.max(1, bathRoomMin || 0), 6, 0.5).map(
                              n => ({
                                label: `${n} bathroom${n > 1 ? "s" : ""} ${
                                  n === Number(property.total_bedrooms)
                                    ? "(Current property)"
                                    : ""
                                }`,
                                value: n
                              })
                            )
                          ]}
                          text={
                            bathRoomMax ? (
                              <Bold>
                                {bathRoomMax} bathroom{bathRoomMax > 1 && "s"}
                              </Bold>
                            ) : (
                              <Bold>Any</Bold>
                            )
                          }
                          value={bathRoomMax}
                          onSelect={setBathRoomMax}
                        />
                      </PillButton>
                    </Row>
                  </Wrapper>
                )}
              </CardBody>
            </Card>
          </Wrapper>
        )}
      </Wrapper>
      <CompMap
        property={property}
        comps={comps}
        selectedComps={selectedComps}
      />
      <Wrapper
        style={{
          flex: 2,
          justifyContent: "flex-start",
          alignItems: "center",
          overflow: "hidden"
        }}
      >
        {loadingComps && (
          <Row style={{ padding: 6 }}>
            <Spin size="small" />
          </Row>
        )}
        {!loadingComps && comps.length > 0 && (
          <Wrapper style={{ width: "100%" }}>
            <Row
              style={{
                paddingLeft: 12,
                paddingRight: 12,
                justifyContent: "space-between"
              }}
            >
              <Copy>Found {comps.length} comparable properties</Copy>
              <PillButton
                innerStyle={{
                  paddingTop: 3,
                  paddingBottom: 3,
                  paddingLeft: 10,
                  paddingRight: 10
                }}
                onPress={() => setSelectedComps([])}
                disabled={!selectedComps.length}
              >
                {selectedComps.length ? (
                  "Clear Selection"
                ) : (
                  <Copy style={{ color: "lightgrey" }}>Clear Selection</Copy>
                )}
              </PillButton>
            </Row>
          </Wrapper>
        )}

        {!loadingComps && comps.length < 1 ? (
          <Row style={{ padding: 6 }}>
            <Copy>
              No comparable properties. Try adjusting your search criteria
            </Copy>
          </Row>
        ) : (
          <Scroll style={{ alignSelf: "stretch", flex: 1 }}>
            {comps.map(comp => (
              <CompItem
                key={comp.id}
                comp={comp}
                open={selectedComps.includes(comp.id)}
                onSelect={() =>
                  setSelectedComps(ids =>
                    !ids.includes(comp.id)
                      ? [...ids, comp.id]
                      : ids.filter(i => i != comp.id)
                  )
                }
              />
            ))}
          </Scroll>
        )}
      </Wrapper>
    </Wrapper>
  );
};
